import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminGscClusterOpportunities } from "@/lib/blog-gsc-opportunities.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Search, ChevronDown, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/admin/blog-gsc-opportunities")({
  component: GscOpportunitiesPage,
  head: () => ({ meta: [{ title: "GSC opportunities · Admin" }, { name: "robots", content: "noindex" }] }),
});

function GscOpportunitiesPage() {
  const load = useServerFn(adminGscClusterOpportunities);
  const q = useQuery({ queryKey: ["admin-gsc-opportunities"], queryFn: () => load() });
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const data = q.data as any;

  return (
    <div>
      <BlogSubNav />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Search Console opportunities by cluster</h1>
          <p className="text-sm text-muted-foreground">
            Why each GSC-gated draft exists. Gate: {data?.gatingRule ?? "rank 6–40 with ≥25 impressions"} over the last{" "}
            {data?.windowDays ?? 90} days.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading Search Console data…</p>
      ) : q.error ? (
        <p className="text-sm text-destructive">Couldn't load opportunities.</p>
      ) : !data?.connected ? (
        <div className="rounded-xl border border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No Search Console data available yet. Once the property is connected and reporting, opportunities appear here and
          the daily GSC drafter starts creating drafts.
        </div>
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            <Stat label="Queries in window" value={data.totals.queries.toLocaleString()} />
            <Stat label="Gated opportunities" value={data.totals.gated.toLocaleString()} />
            <Stat label="Impressions" value={data.totals.impressions.toLocaleString()} />
          </div>

          <div className="space-y-3">
            {data.clusters.map((c: any) => {
              const key = c.clusterId ?? "unassigned";
              const isOpen = open[key] ?? c.totals.gated > 0;
              return (
                <div key={key} className="rounded-xl border border-border bg-background shadow-sm">
                  <button
                    className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
                    onClick={() => setOpen((s) => ({ ...s, [key]: !isOpen }))}
                  >
                    <span className="flex items-center gap-2 font-medium">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {c.clusterName}
                      {c.status ? (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-xs text-muted-foreground">{c.status}</span>
                      ) : null}
                    </span>
                    <span className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span>{c.totals.gated} gated</span>
                      <span>{c.totals.impressions.toLocaleString()} impr.</span>
                      <span>
                        rank{" "}
                        {c.rankRange ? `${c.rankRange.min.toFixed(1)}–${c.rankRange.max.toFixed(1)}` : "—"}
                      </span>
                      <span>{data.windowDays}d</span>
                    </span>
                  </button>

                  {isOpen ? (
                    c.queries.length === 0 ? (
                      <p className="px-4 pb-4 text-sm text-muted-foreground">No queries matched this cluster.</p>
                    ) : (
                      <div className="overflow-x-auto border-t border-border">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium">Query</th>
                              <th className="px-4 py-2 text-right font-medium">Impr.</th>
                              <th className="px-4 py-2 text-right font-medium">Clicks</th>
                              <th className="px-4 py-2 text-right font-medium">Avg. rank</th>
                              <th className="px-4 py-2 text-left font-medium">Gate</th>
                              <th className="px-4 py-2 text-left font-medium">Drafts created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.queries.map((row: any) => (
                              <tr key={row.query} className="border-t border-border/60">
                                <td className="px-4 py-2">
                                  <span className="inline-flex items-center gap-2">
                                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                                    {row.query}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums">{row.impressions.toLocaleString()}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{row.clicks.toLocaleString()}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{row.position.toFixed(1)}</td>
                                <td className="px-4 py-2">
                                  <span
                                    className={`rounded-full px-2 py-0.5 text-xs ${
                                      row.gated
                                        ? "bg-primary/10 text-primary"
                                        : "bg-muted text-muted-foreground"
                                    }`}
                                  >
                                    {row.gated ? "Eligible" : "Below gate"}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  {row.drafts.length === 0 ? (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  ) : (
                                    <ul className="space-y-1">
                                      {row.drafts.map((d: any) => (
                                        <li key={d.id} className="text-xs">
                                          <a className="underline" href={`/admin/posts/${d.id}`}>
                                            {d.title}
                                          </a>{" "}
                                          <span className="text-muted-foreground">({d.status})</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
